import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Plus, Loader2 } from "lucide-react";
import api from "../lib/api";

const STATUS_LABEL = {
  payment_pending: { t: "Awaiting payment", c: "bg-gray-100 text-gray-600" },
  under_review: { t: "Under review", c: "bg-amber-100 text-amber-700" },
  needs_revision: { t: "Needs revision", c: "bg-orange-100 text-orange-700" },
  approved: { t: "Published", c: "bg-green-100 text-green-700" },
  rejected: { t: "Declined", c: "bg-red-100 text-red-700" },
  draft: { t: "Draft", c: "bg-gray-100 text-gray-600" },
};
const PAY_LABEL = {
  paid: { t: "Paid", c: "text-green-700" },
  pending: { t: "Pending", c: "text-gray-500" },
  unpaid: { t: "Unpaid", c: "text-gray-500" },
  refunded: { t: "Refunded", c: "text-red-600" },
};

export default function MyPublishing() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/publishing/mine").then((r) => setOrders(r.data.orders || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  return (
    <div className="pti-container py-10" data-testid="my-publishing-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-4xl font-black">My Publishing</h1>
        <Link to="/advertise" className="bg-pti-red text-white px-5 py-2.5 text-sm font-bold hover:bg-pti-redhover transition-colors">Browse packages</Link>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><Loader2 className="mx-auto animate-spin" size={28} /></div>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center text-gray-500" data-testid="my-publishing-empty">
          <FileText className="mx-auto text-gray-300" size={40} />
          <p className="mt-3">You haven't purchased any publishing packages yet.</p>
          <Link to="/advertise" className="inline-block mt-5 border border-pti-black px-6 py-3 font-bold hover:bg-pti-black hover:text-white transition-colors">See publishing packages</Link>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {orders.map((o) => {
            const pay = PAY_LABEL[o.payment_status] || PAY_LABEL.pending;
            const isBundle = (o.credits_total || 1) > 1;
            const canSubmit = o.payment_status === "paid" && o.credits_remaining > 0;
            return (
              <div key={o.id} className="border border-gray-200" data-testid={`my-order-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3 px-5 py-4 border-b border-gray-200 bg-gray-50">
                  <div>
                    <div className="font-display text-lg font-bold">{o.package_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      ${o.amount} · <span className={`font-bold ${pay.c}`}>{pay.t}</span>
                      {isBundle && <> · {o.credits_remaining}/{o.credits_total} submissions left</>}
                    </div>
                  </div>
                  {canSubmit && (
                    <Link to={`/publish?package=${o.package}&order=${o.id}`} data-testid={`my-order-submit-${o.id}`}
                      className="flex items-center gap-1.5 bg-pti-black text-white px-4 py-2 text-sm font-bold hover:bg-pti-red transition-colors">
                      <Plus size={14} /> Submit article
                    </Link>
                  )}
                </div>
                <div className="divide-y divide-gray-100">
                  {(o.articles || []).length === 0 && <div className="px-5 py-4 text-sm text-gray-400">No articles submitted on this order yet.</div>}
                  {(o.articles || []).map((a) => {
                    const st = STATUS_LABEL[a.status] || { t: a.status, c: "bg-gray-100 text-gray-600" };
                    return (
                      <div key={a.id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`my-article-${a.id}`}>
                        <div className="min-w-0">
                          <p className="font-bold text-sm truncate">{a.title}</p>
                          <p className="text-xs text-gray-400">{a.category}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 ${st.c}`}>{st.t}</span>
                          {a.status === "approved" && <Link to={`/article/${a.slug}`} className="text-sm font-bold text-pti-red">View</Link>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
